import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Row,
  Col
} from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BiTrash } from 'react-icons/bi';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import { FSBTable } from 'components/common/FSBTable';
import { authContext } from 'contexts/AuthContext';
import { axiosDelete, axiosGetAPI, axiosPost, axiosUpdate } from 'services';

const dayOptions = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

const getMarketFreezeSchedulerUrl = () => {
  const baseUrl =
    process.env.REACT_APP_REST_API_ENDPOINT ||
    (process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api/` : '/api/');

  return `${baseUrl.replace(/\/?$/, '/')}market-freeze/scheduler`;
};

const getColumns = (
  toggleStatus,
  openDeleteConfirmation,
  deletingId,
  updatingId,
) => [
  {
    header: 'START DAY TIME',
    accessorKey: 'startDay',
    cell: (info) => {
      const row = info.row.original;
      return `${row.startDay}, ${row.startTime}`;
    },
  },
  {
    header: 'END DAY TIME',
    accessorKey: 'endDay',
    cell: (info) => {
      const row = info.row.original;
      return `${row.endDay}, ${row.endTime}`;
    },
  },
  {
    header: 'REASON',
    accessorKey: 'reason',
  },
  {
    header: 'STATUS',
    accessorKey: 'enabled',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="form-check form-switch d-inline-block" style={{ margin: 0 }}>
          <Input
            type="switch"
            role="switch"
            checked={Boolean(row.enabled)}
            onChange={() => toggleStatus(row)}
            disabled={updatingId === row.id}
            className="cursor-pointer shadow-none"
            style={{ width: '50px', height: '20px' }}
          />
        </div>
      );
    },
  },
  {
    header: 'FLAG',
    accessorKey: 'currentlyActive',
    cell: (info) => {
      const row = info.row.original;

      if (!row.currentlyActive) return null;

      return (
        <span
          className="badge border border-0"
          style={{ backgroundColor: '#CBE2F9', color: '#000' }}
        >
          Active
        </span>
      );
    },
  },
  {
    header: 'DELETE',
    accessorKey: 'delete',
    enableSorting: false,
    cell: (info) => {
      const row = info.row.original;
      const isDeleting = deletingId === row.id;

      return (
        <Button
          color="link"
          className="text-danger p-0 d-inline-flex align-items-center"
          disabled={isDeleting}
          aria-label="Delete schedule"
          title="Delete"
          onClick={() => openDeleteConfirmation(row.id)}
        >
          <BiTrash />
        </Button>
      );
    },
  },
];

const MarketFreezeSchedule = () => {
  const { auth } = useContext(authContext);
  const [scheduleList, setScheduleList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [showDeleteRecord, setShowDeleteRecord] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEndDay, setSelectedEndDay] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');

  const schedulerUrl = useMemo(() => getMarketFreezeSchedulerUrl(), []);

  const fetchSchedules = useCallback(async () => {
    if (!auth?.data?.token) return;

    setLoading(true);
    const response = await axiosGetAPI(schedulerUrl, auth);
    setLoading(false);

    if (response?.isSuccessful && !response?.data?.error) {
      setScheduleList(response?.data?.data || []);
      return;
    }

    toast.error(
      response?.data?.message ||
        response?.message ||
        'Unable to fetch market freeze scheduler.',
    );
  }, [auth, schedulerUrl]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  useEffect(() => {
    const msUntilNextMinute = () => {
      const now = new Date();
      return ((60 - now.getSeconds()) * 1000) - now.getMilliseconds();
    };

    let intervalId;
    const timeoutId = setTimeout(() => {
      fetchSchedules();
      intervalId = setInterval(fetchSchedules, 60000);
    }, msUntilNextMinute());

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [fetchSchedules]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      // Reset form when opening
      setSelectedDay(null);
      setSelectedEndDay(null);
      setStartTime('');
      setEndTime('');
      setReason('');
    }
  };

  const handleAddSchedule = async () => {
    const trimmedReason = reason.trim();

    if (
      !selectedDay ||
      !selectedEndDay ||
      !startTime ||
      !endTime ||
      !trimmedReason
    ) {
      toast.error('Please fill all required fields.');
      return;
    }

    const payload = {
      startDay: selectedDay.value,
      startTime,
      endDay: selectedEndDay.value,
      endTime,
      reason: trimmedReason,
      enabled: true,
    };

    setIsSubmitting(true);
    const response = await axiosPost(
      schedulerUrl,
      payload,
      { 'Content-Type': 'application/json' },
      auth,
    );
    setIsSubmitting(false);

    if (response?.isSuccessful && !response?.data?.error) {
      toast.success(response?.data?.message || 'Scheduler saved successfully');
      toggleModal();
      fetchSchedules();
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new Event('market-freeze-schedule-updated'));
      }
      return;
    }

    toast.error(
      response?.data?.message ||
        response?.message ||
        'Unable to save market freeze scheduler.',
    );
  };

  const toggleStatus = useCallback(
    async (row) => {
      if (!row?.id || !auth?.data?.token) return;

      const action = row.enabled ? 'disable' : 'enable';
      const nextEnabled = !row.enabled;
      const actionUrl = `${schedulerUrl}/${row.id}/${action}`;

      setUpdatingId(row.id);
      const response = await axiosUpdate(actionUrl, {}, auth?.data?.token);
      setUpdatingId(null);

      if (response?.isSuccessful && !response?.data?.error) {
        toast.success(
          response?.data?.message ||
            `Scheduler ${nextEnabled ? 'enabled' : 'disabled'} successfully`,
        );
        fetchSchedules();
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new Event('market-freeze-schedule-updated'));
        }
        return;
      }

      toast.error(
        response?.data?.message ||
          response?.message ||
          `Unable to ${action} market freeze scheduler.`,
      );
    },
    [auth?.data?.token, fetchSchedules, schedulerUrl],
  );

  const openDeleteConfirmation = useCallback((id) => {
    setDeleteRowId(id);
    setShowDeleteRecord(true);
  }, []);

  const closeDeleteConfirmation = useCallback(() => {
    if (deletingId) return;

    setDeleteRowId(null);
    setShowDeleteRecord(false);
  }, [deletingId]);

  const handleDeleteSchedule = useCallback(
    async () => {
      if (!deleteRowId) return;

      setDeletingId(deleteRowId);
      const response = await axiosDelete(
        `${schedulerUrl}/${deleteRowId}`,
        auth?.data?.token,
      );
      setDeletingId(null);

      if (response?.isSuccessful && !response?.data?.error) {
        toast.success(
          response?.data?.message || 'Scheduler deleted successfully',
        );
        closeDeleteConfirmation();
        fetchSchedules();
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new Event('market-freeze-schedule-updated'));
        }
        return;
      }

      toast.error(
        response?.data?.message ||
          response?.message ||
          'Unable to delete market freeze scheduler.',
      );
    },
    [
      auth?.data?.token,
      closeDeleteConfirmation,
      deleteRowId,
      fetchSchedules,
      schedulerUrl,
    ],
  );

  const columns = useMemo(
    () => getColumns(toggleStatus, openDeleteConfirmation, deletingId, updatingId),
    [deletingId, openDeleteConfirmation, toggleStatus, updatingId],
  );

  return (
    <Card className="border-0 card-box page-card-box">
      <CardBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h4 className="f-16 fw-semibold mb-1">
              Automated Schedule for market freeze
            </h4>
          </div>
          <div>
            <Button
              color="primary"
              className="px-4 fw-semibold rounded-pill"
              onClick={toggleModal}
            >
              Add Slot
            </Button>
          </div>
        </div>

        <FSBTable
          data={scheduleList}
          columns={columns}
          loading={loading}
          tableClass="action-table"
          wrapperClass="min-h-table table-responsive word-break"
          showPagination={false}
          showSearch={false}
          showFilters={false}
          showDelete={false}
        />

        <DeleteConfirmationAlert
          open={showDeleteRecord}
          title="Delete"
          message="Do you really want to remove this record?"
          cancelButtonLabel="Close"
          addButtonLabel="Delete"
          loading={Boolean(deletingId)}
          onConfirm={handleDeleteSchedule}
          onCancel={closeDeleteConfirmation}
          toggleAlert={closeDeleteConfirmation}
        />

        {/* Add Slot Modal */}
        <Modal isOpen={isModalOpen} toggle={toggleModal} centered>
          <ModalHeader toggle={toggleModal}>Add Slot</ModalHeader>
          <ModalBody>
            <h6 className="fw-semibold mb-3 text-muted">Start</h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Day <span className="text-danger">*</span></Label>
                  <Select
                    options={dayOptions}
                    value={selectedDay}
                    onChange={setSelectedDay}
                    placeholder="Select..."
                    classNamePrefix="react-select"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Time <span className="text-danger">*</span></Label>
                  <div className="position-relative">
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="border-field bg-white pe-5"
                      style={{ minHeight: 42 }}
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3 text-muted"
                      aria-hidden="true"
                    >
                      <i className="ti ti-clock" />
                    </span>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <h6 className="fw-semibold mb-3 mt-2 text-muted">End</h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Day <span className="text-danger">*</span></Label>
                  <Select
                    options={dayOptions}
                    value={selectedEndDay}
                    onChange={setSelectedEndDay}
                    placeholder="Select..."
                    classNamePrefix="react-select"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Time <span className="text-danger">*</span></Label>
                  <div className="position-relative">
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="border-field bg-white pe-5"
                      style={{ minHeight: 42 }}
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3 text-muted"
                      aria-hidden="true"
                    >
                      <i className="ti ti-clock" />
                    </span>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mt-2">
              <Label className="fw-semibold">Reason <span className="text-danger">*</span></Label>
              <Input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason"
                className="border-field bg-white"
                style={{ minHeight: 42 }}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter className="border-0 pb-4 pe-4">
            <Button
              color="secondary"
              outline
              size="sm"
              className="px-4 rounded-pill"
              onClick={toggleModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              color="warning"
              size="sm"
              className="px-4 rounded-pill border-0 text-dark"
              onClick={handleAddSchedule}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Schedule'}
            </Button>
          </ModalFooter>
        </Modal>

      </CardBody>
    </Card>
  );
};

export default MarketFreezeSchedule;
